import {
  Button,
  Css,
  Icon,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Palette,
  Tag,
  useComputed,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ReactNode } from "react";
import {
  parseAiString,
  pendingProposedValue,
  rejectAiProposal,
} from "src/components/formFields/ai/utils/bindAiProposal";
import { AiFieldProposalStatus } from "src/generated/graphqlTypes";
import {
  MaterialVariantFormValue,
  VariantImageAsset,
} from "src/routes/libraries/material-catalog/components/materialListingForm";
import { MATERIAL_PHOTO_EMPTY_STATE_IMG_URL } from "src/utils/constants";

/** Sized so the two photos and the arrow between them fill the `lg` modal's body. */
const COMPARED_IMAGE_SIZE_PX = 248;

type CompareVariantImageModalProps = { formRow: ObjectState<MaterialVariantFormValue> };

/**
 * Side-by-side review of the photo an import swapped in, against the one it replaced.
 *
 * Accept keeps the import's photo; Ignore drops it, which leaves the variant on its original.
 * Then the proposal is resolved, so the tile stops flagging it.
 */
export function CompareVariantImageModal(props: CompareVariantImageModalProps) {
  const { formRow } = props;
  const { closeModal } = useModal();
  const change = useComputed(() => proposedImageChange(formRow), [formRow]);

  function accept() {
    change?.proposal.status.set(AiFieldProposalStatus.Accepted);
    closeModal();
  }

  function ignore() {
    if (change) {
      formRow.images.remove(change.proposed.value);
      rejectAiProposal(change.proposal);
    }
    closeModal();
  }

  return (
    <>
      <ModalHeader>Compare Image</ModalHeader>
      <ModalBody>
        <div css={Css.df.aic.jcsb.$}>
          <ComparedImage kind="original" label="Original" src={imageSrc(change?.original)} />
          <Icon icon="arrowRight" color={Palette.Gray900} inc={6} />
          <ComparedImage
            kind="proposed"
            label={
              <div css={Css.df.aic.gapPx(12).purple600.$}>
                <Tag text="AI suggested photo" preventTooltip icon="aiStar" type="ai" iconOnly />
                New
              </div>
            }
            src={imageSrc(change?.proposed)}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Ignore" variant="secondary" onClick={ignore} />
        <Button label="Accept" variant="ai" onClick={accept} />
      </ModalFooter>
    </>
  );
}

type ComparedImageProps = { kind: "original" | "proposed"; label: ReactNode; src: string };

function ComparedImage(props: ComparedImageProps) {
  const { kind, label, src } = props;
  const tid = useTestIds({}, "compareImage");
  return (
    <div css={Css.df.fdc.gapPx(14).$}>
      <div css={Css.mdSb.gray900.$}>{label}</div>
      <div css={Css.sqPx(COMPARED_IMAGE_SIZE_PX).ba.bw2.bcGray300.br8.bgWhite.oh.$}>
        <img {...tid[kind]} css={Css.w100.h100.objectContain.$} src={src} alt="" />
      </div>
    </div>
  );
}

type ImageRow = ObjectState<VariantImageAsset>;

/** The photo an import swapped in, and the one it would leave behind if ignored. */
export type ProposedImageChange = {
  proposal: NonNullable<ReturnType<typeof findImageProposal>>;
  proposed: ImageRow;
  /** Undefined when the variant had no photo before the import. */
  original: ImageRow | undefined;
};

/**
 * The agent's proposal for this variant's photo, if it found one.
 *
 * Replacing or deleting the photo is the user overriding it; left alone, the Create sweep accepts it.
 */
export function findImageProposal(variant: ObjectState<MaterialVariantFormValue>) {
  // "imageUrl" is the name of the afp field we use to store the MVI asset url
  return variant.fieldProposals.rows.find((p) => p.fieldName.value === "imageUrl");
}

/**
 * The still-unreviewed photo change on this variant, once its photo has landed.
 *
 * The import downloads the agent's photo server-side and puts it ahead of the existing one, so the
 * change is "the image from the proposal's url" vs. "whichever image would be featured without it".
 */
export function proposedImageChange(
  variant: ObjectState<MaterialVariantFormValue> | undefined,
): ProposedImageChange | undefined {
  if (!variant) return undefined;
  const proposal = findImageProposal(variant);
  const url = pendingProposedValue(proposal, parseAiString);
  const proposed = url && variant.images.rows.find((img) => img.sourceUrl.value === url);
  if (!proposal || !proposed) return undefined;
  const original = variant.images.rows
    .filter((img) => img !== proposed)
    .sortBy((img) => img.sortOrder.value ?? 0).first;
  return { proposal, proposed, original };
}

function imageSrc(image: ImageRow | undefined): string {
  return image?.attachmentUrl.value || image?.downloadUrl.value || MATERIAL_PHOTO_EMPTY_STATE_IMG_URL;
}
